import * as React from 'react'
import {
  Box, Typography, TextField, Button, ToggleButton, ToggleButtonGroup,
  Autocomplete, Chip, CircularProgress, Alert, Divider, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Accordion,
  AccordionSummary, AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BarChartIcon from '@mui/icons-material/BarChart'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import TableChartIcon from '@mui/icons-material/TableChart'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'

import Toolbar from '../components/Toolbar'
import competitorService from '../services/competitor'

// ── helpers ──────────────────────────────────────────────────────────────────

function parseBase64Csv(base64) {
  const text = atob(base64)
  const lines = text.trim().split('\n')
  const headers = lines[0].split(',')
  const rows = lines.slice(1).map((l) => l.split(','))
  return { headers, rows }
}

// ── sub-components ────────────────────────────────────────────────────────────

function SummarySection({ summaries }) {
  if (!summaries?.length) return null
  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <TextSnippetIcon sx={{ color: '#667eea' }} />
        <Typography variant="h6" fontWeight={600}>AI Summary</Typography>
      </Box>
      {summaries.map((s, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
          <Chip label={s.ticker} size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {s.summary}
          </Typography>
        </Paper>
      ))}
    </Box>
  )
}

function TableSection({ tables }) {
  if (!tables?.length) return null
  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <TableChartIcon sx={{ color: '#667eea' }} />
        <Typography variant="h6" fontWeight={600}>Financial Data</Typography>
      </Box>
      {tables.map((t, i) => {
        const { headers, rows } = parseBase64Csv(t.csv_download.content_base64)
        return (
          <Accordion key={i} defaultExpanded={tables.length === 1} variant="outlined" sx={{ mb: 1, borderRadius: '8px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Chip label={t.ticker} size="small" color="primary" variant="outlined" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">{t.company_name}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer sx={{ maxHeight: 380 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {headers.map((h, j) => (
                        <TableCell key={j} sx={{ fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap', backgroundColor: '#f5f5f5' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, ri) => (
                      <TableRow key={ri} hover>
                        {row.map((cell, ci) => (
                          <TableCell key={ci} sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}

function ChartsSection({ graphs }) {
  if (!graphs?.length) return null
  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <BarChartIcon sx={{ color: '#667eea' }} />
        <Typography variant="h6" fontWeight={600}>Charts</Typography>
      </Box>
      {graphs.map((g, i) => (
        <Box key={i} mb={2}>
          <Chip label={g.ticker} size="small" color="primary" variant="outlined" sx={{ mb: 1.5 }} />
          <Box display="flex" flexWrap="wrap" gap={2}>
            {g.graphs.map((chart, ci) => (
              <Box key={ci} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                <Typography variant="caption" display="block" sx={{ px: 1.5, py: 0.5, backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                  {chart.metric.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </Typography>
                <img
                  src={`data:image/png;base64,${chart.image_base64}`}
                  alt={chart.metric}
                  style={{ display: 'block', maxWidth: '100%' }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

// ── main page ─────────────────────────────────────────────────────────────────

const FLAG_OPTIONS = [
  { value: '1', label: 'Summary' },
  { value: '2', label: 'Table' },
  { value: '3', label: 'Charts' },
  { value: '4', label: 'All' },
]

export default function FinancialComparison() {
  const [competitors, setCompetitors] = React.useState([])
  const [parentCik, setParentCik] = React.useState('')
  const [selectedTickers, setSelectedTickers] = React.useState([])
  const [question, setQuestion] = React.useState('')
  const [reportFlag, setReportFlag] = React.useState('4')
  const [loading, setLoading] = React.useState(false)
  const [result, setResult] = React.useState(null)
  const [error, setError] = React.useState(null)

  React.useEffect(() => {
    competitorService.listCompetitors()
      .then((res) => setCompetitors(res.data))
      .catch(() => {})
  }, [])

  const handleRun = async () => {
    if (!parentCik.trim() || selectedTickers.length === 0 || !question.trim()) return
    setLoading(true)
    setResult(null)
    setError(null)
    try {
      const res = await competitorService.financialComparison({
        parent_cik: parentCik.trim(),
        target_tickers: selectedTickers,
        report_flag: parseInt(reportFlag),
        user_question: question,
      })
      setResult(res.data)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Comparison failed. Check the backend logs.')
    } finally {
      setLoading(false)
    }
  }

  const hasResults = result && (result.text_summary || result.tables || result.graphs)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.paper' }}>
      <Toolbar />

      <Box sx={{ flex: 1, overflow: 'auto', p: 3, backgroundColor: '#fafafa' }}>
        <Typography variant="h5" fontWeight={700} mb={0.5}>Financial Comparison</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Compare your company's financials against saved competitors using SEC EDGAR data.
        </Typography>

        {/* ── Form ── */}
        <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">

            <TextField
              label="Your Company CIK"
              placeholder="e.g. 0000320193"
              value={parentCik}
              onChange={(e) => setParentCik(e.target.value)}
              size="small"
              sx={{ width: 200 }}
              inputProps={{ maxLength: 20 }}
              helperText="10-digit SEC CIK"
            />

            <Autocomplete
              multiple
              options={competitors.map((c) => c.ticker)}
              value={selectedTickers}
              onChange={(_, v) => setSelectedTickers(v)}
              renderTags={(val, getTagProps) =>
                val.map((option, index) => (
                  <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Competitors" placeholder="Select tickers" size="small"
                  helperText="Add competitors in Settings → Competitors" />
              )}
              sx={{ minWidth: 260 }}
              noOptionsText="No competitors saved — go to Settings → Competitors"
            />

            <TextField
              label="Your question"
              placeholder="e.g. How does our revenue compare last quarter?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 280 }}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleRun()}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Report type</Typography>
              <ToggleButtonGroup
                value={reportFlag}
                exclusive
                onChange={(_, v) => v && setReportFlag(v)}
                size="small"
              >
                {FLAG_OPTIONS.map((o) => (
                  <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none', px: 1.5 }}>
                    {o.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleRun}
              disabled={loading || !parentCik.trim() || selectedTickers.length === 0 || !question.trim()}
              sx={{ textTransform: 'none', alignSelf: 'flex-start', mt: 0.5, minWidth: 120 }}
            >
              {loading ? 'Running…' : 'Run'}
            </Button>
          </Box>
        </Paper>

        {/* ── Error ── */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Results ── */}
        {hasResults && (
          <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Results</Typography>
            <Divider sx={{ mb: 2 }} />
            <SummarySection summaries={result.text_summary} />
            <TableSection tables={result.tables} />
            <ChartsSection graphs={result.graphs} />
          </Paper>
        )}
      </Box>
    </Box>
  )
}
